// API specification — single source of truth for the docs page.
// Adding a new endpoint? Add it here and it appears on /api-docs automatically.

const HOST = "https://pdf-tool.km-bizz.com";

// ── helpers to build example snippets in different languages ────────────────
const curlSnippet = (method, path, { headers = {}, body, form } = {}) => {
    const lines = [`curl -X ${method} '${HOST}${path}'`];
    Object.entries(headers).forEach(([k, v]) => lines.push(`  -H '${k}: ${v}'`));
    if (body) lines.push(`  -d '${body}'`);
    if (form) form.forEach((f) => lines.push(`  -F '${f}'`));
    return lines.join(" \\\n");
};

const jsSnippet = (method, path, { headers = {}, body, form } = {}) => {
    const opts = { method };
    if (headers && Object.keys(headers).length) opts.headers = headers;
    if (body) opts.body = body;

    if (form) {
        return [
            `const formData = new FormData();`,
            ...form.map((f) => {
                const [k, v] = f.split("=");
                return v?.startsWith("@")
                    ? `formData.append('${k}', fileInput.files[0]);`
                    : `formData.append('${k}', '${v}');`;
            }),
            ``,
            `const res = await fetch('${HOST}${path}', {`,
            `    method: '${method}',`,
            `    body: formData,`,
            `});`,
            `const data = await res.json();`,
        ].join("\n");
    }

    return [
        `const res = await fetch('${HOST}${path}', ${JSON.stringify(opts, null, 2)});`,
        `const data = await res.json();`,
    ].join("\n");
};

const pythonSnippet = (method, path, { headers = {}, body, form } = {}) => {
    if (form) {
        return [
            `import requests`,
            ``,
            `files = {`,
            ...form.filter(f => f.includes("@")).map(f => {
                const [k] = f.split("=");
                return `    '${k}': open('document.pdf', 'rb'),`;
            }),
            `}`,
            form.some(f => !f.includes("@"))
                ? `data = {\n${form.filter(f => !f.includes("@")).map(f => {
                    const [k, v] = f.split("=");
                    return `    '${k}': '${v}',`;
                }).join("\n")}\n}`
                : null,
            ``,
            `res = requests.${method.toLowerCase()}(`,
            `    '${HOST}${path}',`,
            `    files=files${form.some(f => !f.includes("@")) ? `,\n    data=data` : ""},`,
            `)`,
            `print(res.json())`,
        ].filter(Boolean).join("\n");
    }
    const lines = [`import requests`, ``, `res = requests.${method.toLowerCase()}(`, `    '${HOST}${path}',`];
    if (headers && Object.keys(headers).length) lines.push(`    headers=${JSON.stringify(headers)},`);
    if (body) lines.push(`    data='${body}',`);
    lines.push(`)`, `print(res.json())`);
    return lines.join("\n");
};

// ── endpoint definitions ────────────────────────────────────────────────────
export const API_SECTIONS = [
    {
        id: "pdf-tools",
        title: "PDF Tools",
        description: "Core PDF processing — merge, compress, convert. Returns the processed file as a binary stream.",
        color: "#FF5A4E",
        endpoints: [
            {
                id: "merge",
                method: "POST",
                path: "/api/pdf/merge",
                title: "Merge multiple PDFs",
                description: "Combines two or more PDF files into a single document. Order matches the upload order.",
                params: [
                    { name: "files", in: "form-data", type: "file[]", required: true, description: "Multiple PDF files. Send the field name 'files' for each file." },
                ],
                response: {
                    contentType: "application/pdf",
                    description: "Binary PDF stream. Save the response body as a file.",
                },
                examples: {
                    curl: curlSnippet("POST", "/api/pdf/merge", {
                        form: ["files=@first.pdf", "files=@second.pdf"],
                    }),
                    javascript: jsSnippet("POST", "/api/pdf/merge", {
                        form: ["files=@file"],
                    }),
                    python: pythonSnippet("POST", "/api/pdf/merge", {
                        form: ["files=@first.pdf", "files=@second.pdf"],
                    }),
                },
            },
            {
                id: "image-to-pdf",
                method: "POST",
                path: "/api/pdf/image/convert-image",
                title: "Convert image to PDF",
                description: "Wraps a single image (PNG, JPG, JPEG, GIF, WEBP) as an A4-sized PDF page.",
                params: [
                    { name: "file", in: "form-data", type: "file", required: true, description: "Single image file." },
                ],
                response: {
                    contentType: "application/pdf",
                    description: "Binary PDF stream.",
                },
                examples: {
                    curl: curlSnippet("POST", "/api/pdf/image/convert-image", {
                        form: ["file=@photo.jpg"],
                    }),
                    javascript: jsSnippet("POST", "/api/pdf/image/convert-image", {
                        form: ["file=@image"],
                    }),
                    python: pythonSnippet("POST", "/api/pdf/image/convert-image", {
                        form: ["file=@photo.jpg"],
                    }),
                },
            },
            {
                id: "compress",
                method: "POST",
                path: "/api/pdf/compress/compress-pdf",
                title: "Compress a PDF",
                description: "Reduces PDF file size. Quality levels: LOW (max compression), MEDIUM (balanced), HIGH (best quality, smallest savings).",
                params: [
                    { name: "file", in: "form-data", type: "file", required: true, description: "PDF file to compress." },
                    { name: "pdfQuality", in: "form-data", type: "enum", required: true, description: "One of: LOW, MEDIUM, HIGH" },
                ],
                response: {
                    contentType: "application/pdf",
                    description: "Compressed PDF binary stream.",
                },
                examples: {
                    curl: curlSnippet("POST", "/api/pdf/compress/compress-pdf", {
                        form: ["file=@large.pdf", "pdfQuality=LOW"],
                    }),
                    javascript: jsSnippet("POST", "/api/pdf/compress/compress-pdf", {
                        form: ["file=@pdf", "pdfQuality=LOW"],
                    }),
                    python: pythonSnippet("POST", "/api/pdf/compress/compress-pdf", {
                        form: ["file=@large.pdf", "pdfQuality=LOW"],
                    }),
                },
            },
        ],
    },
    {
        id: "pdf-editor",
        title: "PDF Editor",
        description: "Session-based PDF editing service. Upload a PDF, get a session ID, edit text blocks, then download the modified file.",
        color: "#F59E0B",
        endpoints: [
            {
                id: "editor-upload",
                method: "POST",
                path: "/editor-api/upload",
                title: "Upload a PDF for editing",
                description: "Starts a new editing session. Returns a session ID used for subsequent edit/download calls.",
                params: [
                    { name: "file", in: "form-data", type: "file", required: true, description: "PDF file to load." },
                ],
                response: {
                    contentType: "application/json",
                    example: JSON.stringify({
                        session_id: "p33LCVs-n-bA-GOq",
                        filename: "document.pdf",
                        pages: 3,
                        pages_info: [
                            { page: 0, width: 595.28, height: 841.89 },
                        ],
                    }, null, 2),
                },
                examples: {
                    curl: curlSnippet("POST", "/editor-api/upload", {
                        form: ["file=@document.pdf"],
                    }),
                    javascript: jsSnippet("POST", "/editor-api/upload", {
                        form: ["file=@pdf"],
                    }),
                    python: pythonSnippet("POST", "/editor-api/upload", {
                        form: ["file=@document.pdf"],
                    }),
                },
            },
            {
                id: "editor-page",
                method: "GET",
                path: "/editor-api/page/{session_id}/{page_num}",
                title: "Fetch a page with editable text blocks",
                description: "Returns the page rendered as a base64 PNG plus all detected text blocks with their positions, fonts, and colors.",
                params: [
                    { name: "session_id", in: "path", type: "string", required: true, description: "From /upload response." },
                    { name: "page_num", in: "path", type: "integer", required: true, description: "Zero-indexed page number." },
                ],
                response: {
                    contentType: "application/json",
                    example: JSON.stringify({
                        page: 0,
                        image: "iVBORw0KGgoAAAA...",
                        width: 595.28,
                        height: 841.89,
                        blocks: [{
                            index: 0,
                            text: "Hello",
                            x: 72, y: 100.5,
                            width: 60, height: 14,
                            font_size: 12,
                            font: "Helvetica",
                            color: [0, 0, 0],
                            block_index: 0,
                        }],
                    }, null, 2),
                },
                examples: {
                    curl: curlSnippet("GET", "/editor-api/page/p33LCVs-n-bA-GOq/0"),
                    javascript: jsSnippet("GET", "/editor-api/page/p33LCVs-n-bA-GOq/0"),
                    python: pythonSnippet("GET", "/editor-api/page/p33LCVs-n-bA-GOq/0"),
                },
            },
            {
                id: "editor-edit",
                method: "POST",
                path: "/editor-api/edit",
                title: "Edit a text block",
                description: "Replaces text in the specified bounding box. The original area is redacted before the new text is drawn.",
                params: [
                    { name: "session_id", in: "body", type: "string", required: true },
                    { name: "page", in: "body", type: "integer", required: true },
                    { name: "block_index", in: "body", type: "integer", required: true },
                    { name: "new_text", in: "body", type: "string", required: true },
                    { name: "x, y, width, height", in: "body", type: "number", required: true, description: "Bounding box in PDF points." },
                    { name: "font_size", in: "body", type: "number", required: true },
                    { name: "font_color", in: "body", type: "[r, g, b]", required: true, description: "Each channel 0.0–1.0." },
                ],
                response: {
                    contentType: "application/json",
                    example: JSON.stringify({ success: true, image: "iVBORw0KGgo..." }, null, 2),
                },
                examples: {
                    curl: curlSnippet("POST", "/editor-api/edit", {
                        headers: { "Content-Type": "application/json" },
                        body: '{"session_id":"abc","page":0,"block_index":2,"new_text":"Replaced","x":72,"y":100,"width":60,"height":14,"font_size":12,"font_color":[0,0,0]}',
                    }),
                    javascript: `const res = await fetch('${HOST}/editor-api/edit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
        session_id: 'abc',
        page: 0,
        block_index: 2,
        new_text: 'Replaced',
        x: 72, y: 100, width: 60, height: 14,
        font_size: 12,
        font_color: [0, 0, 0],
    }),
});
const data = await res.json();`,
                    python: `import requests

res = requests.post(
    '${HOST}/editor-api/edit',
    json={
        'session_id': 'abc',
        'page': 0,
        'block_index': 2,
        'new_text': 'Replaced',
        'x': 72, 'y': 100, 'width': 60, 'height': 14,
        'font_size': 12,
        'font_color': [0, 0, 0],
    },
)
print(res.json())`,
                },
            },
            {
                id: "editor-download",
                method: "GET",
                path: "/editor-api/download/{session_id}",
                title: "Download the edited PDF",
                description: "Returns the current state of the session's PDF as a binary download.",
                params: [
                    { name: "session_id", in: "path", type: "string", required: true },
                ],
                response: {
                    contentType: "application/pdf",
                    description: "Binary PDF stream with Content-Disposition: attachment.",
                },
                examples: {
                    curl: curlSnippet("GET", "/editor-api/download/p33LCVs-n-bA-GOq"),
                    javascript: jsSnippet("GET", "/editor-api/download/p33LCVs-n-bA-GOq"),
                    python: pythonSnippet("GET", "/editor-api/download/p33LCVs-n-bA-GOq"),
                },
            },
        ],
    },
    {
        id: "stats",
        title: "Statistics",
        description: "Public usage counters per tool. Read-only — useful for showing 'X PDFs merged today' badges.",
        color: "#0F9D58",
        endpoints: [
            {
                id: "stats-summary",
                method: "GET",
                path: "/api/stats/summary",
                title: "Get usage summary",
                description: "Returns total exports, total bytes, last-7-days counts, and per-tool breakdown.",
                params: [],
                response: {
                    contentType: "application/json",
                    example: JSON.stringify({
                        totalExports: 12847,
                        totalBytes: 4509715200,
                        last7Days: 312,
                        perTool: {
                            "image-to-pdf": { count: 3421, last7Days: 87, totalBytes: 1180000000 },
                            "pdf-merge": { count: 5102, last7Days: 143, totalBytes: 2147000000 },
                            "pdf-compress": { count: 2890, last7Days: 62, totalBytes: 870000000 },
                            "pdf-edit": { count: 1434, last7Days: 20, totalBytes: 280000000 },
                        },
                    }, null, 2),
                },
                examples: {
                    curl: curlSnippet("GET", "/api/stats/summary"),
                    javascript: jsSnippet("GET", "/api/stats/summary"),
                    python: pythonSnippet("GET", "/api/stats/summary"),
                },
            },
            {
                id: "stats-log",
                method: "POST",
                path: "/api/stats/log",
                title: "Log an export event",
                description: "Called internally by the frontend after each successful export. Returns 202 Accepted with no body.",
                params: [
                    { name: "toolId", in: "body", type: "string", required: true, description: "One of: image-to-pdf, pdf-merge, pdf-compress, pdf-edit" },
                    { name: "fileSizeBytes", in: "body", type: "integer", required: false, description: "Output file size for analytics." },
                ],
                response: {
                    contentType: "—",
                    description: "202 Accepted, no body.",
                },
                examples: {
                    curl: curlSnippet("POST", "/api/stats/log", {
                        headers: { "Content-Type": "application/json" },
                        body: '{"toolId":"pdf-merge","fileSizeBytes":123456}',
                    }),
                    javascript: `await fetch('${HOST}/api/stats/log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ toolId: 'pdf-merge', fileSizeBytes: 123456 }),
});`,
                    python: `import requests

requests.post(
    '${HOST}/api/stats/log',
    json={'toolId': 'pdf-merge', 'fileSizeBytes': 123456},
)`,
                },
            },
        ],
    },
    {
        id: "comments",
        title: "Comments & Feedback",
        description: "Public feedback wall and kanban-board moderation. Rate-limited to 5 posts per IP per hour.",
        color: "#2563EB",
        endpoints: [
            {
                id: "comments-list",
                method: "GET",
                path: "/api/comments",
                title: "List all comments",
                description: "Returns every public comment, most recent first.",
                params: [],
                response: {
                    contentType: "application/json",
                    example: JSON.stringify([{
                        id: 42,
                        authorName: "Alice",
                        toolId: "pdf-merge",
                        body: "Love the new compress quality options!",
                        status: "NEW",
                        createdAt: "2026-05-13T14:22:01Z",
                    }], null, 2),
                },
                examples: {
                    curl: curlSnippet("GET", "/api/comments"),
                    javascript: jsSnippet("GET", "/api/comments"),
                    python: pythonSnippet("GET", "/api/comments"),
                },
            },
            {
                id: "comments-create",
                method: "POST",
                path: "/api/comments",
                title: "Submit feedback",
                description: "Public comment submission. Returns 429 if you exceed 5 posts per hour.",
                params: [
                    { name: "authorName", in: "body", type: "string", required: true, description: "Max 120 chars." },
                    { name: "toolId", in: "body", type: "string", required: false, description: "Defaults to 'general'." },
                    { name: "body", in: "body", type: "string", required: true, description: "Max 4000 chars." },
                ],
                response: {
                    contentType: "application/json",
                    example: JSON.stringify({
                        id: 43,
                        authorName: "Alice",
                        toolId: "general",
                        body: "Great tool!",
                        status: "NEW",
                        createdAt: "2026-05-14T09:00:00Z",
                    }, null, 2),
                },
                examples: {
                    curl: curlSnippet("POST", "/api/comments", {
                        headers: { "Content-Type": "application/json" },
                        body: '{"authorName":"Alice","toolId":"pdf-merge","body":"Great tool!"}',
                    }),
                    javascript: `await fetch('${HOST}/api/comments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
        authorName: 'Alice',
        toolId: 'pdf-merge',
        body: 'Great tool!',
    }),
});`,
                    python: `import requests

requests.post(
    '${HOST}/api/comments',
    json={'authorName': 'Alice', 'toolId': 'pdf-merge', 'body': 'Great tool!'},
)`,
                },
            },
            {
                id: "comments-board",
                method: "GET",
                path: "/api/comments/board",
                title: "Kanban view",
                description: "Returns comments grouped by status — NEW, ACKNOWLEDGED, IN_PROGRESS, RESOLVED.",
                params: [],
                response: {
                    contentType: "application/json",
                    example: JSON.stringify({
                        NEW: [{ id: 42, body: "...", status: "NEW" }],
                        ACKNOWLEDGED: [],
                        IN_PROGRESS: [],
                        RESOLVED: [],
                    }, null, 2),
                },
                examples: {
                    curl: curlSnippet("GET", "/api/comments/board"),
                    javascript: jsSnippet("GET", "/api/comments/board"),
                    python: pythonSnippet("GET", "/api/comments/board"),
                },
            },
            {
                id: "comments-status",
                method: "PATCH",
                path: "/api/comments/{id}/status",
                title: "Change a comment's status",
                description: "Move a comment between kanban columns.",
                params: [
                    { name: "id", in: "path", type: "integer", required: true },
                    { name: "status", in: "body", type: "string", required: true, description: "NEW, ACKNOWLEDGED, IN_PROGRESS, or RESOLVED" },
                ],
                response: {
                    contentType: "application/json",
                    example: JSON.stringify({ id: 42, status: "IN_PROGRESS" }, null, 2),
                },
                examples: {
                    curl: curlSnippet("PATCH", "/api/comments/42/status", {
                        headers: { "Content-Type": "application/json" },
                        body: '{"status":"IN_PROGRESS"}',
                    }),
                    javascript: `await fetch('${HOST}/api/comments/42/status', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'IN_PROGRESS' }),
});`,
                    python: `import requests

requests.patch(
    '${HOST}/api/comments/42/status',
    json={'status': 'IN_PROGRESS'},
)`,
                },
            },
        ],
    },
];

export const API_HOST = HOST;
